'use client';

import React, { useState, useRef, useEffect } from 'react';
import { TextInput, Loader, Paper, Text, Stack, Group } from '@mantine/core';
import { IconMapPin, IconSearch } from '@tabler/icons-react';
import type { TextInputProps, PaperProps } from '@mantine/core';

interface LocationOption {
  label: string;
  value: string;
  lat?: number;
  lng?: number;
}

interface LocationAutocompleteStyles {
  container?: React.CSSProperties;
  input?: React.CSSProperties;
  dropdown?: React.CSSProperties;
  suggestion?: React.CSSProperties;
  selectedSuggestion?: React.CSSProperties;
  noResults?: React.CSSProperties;
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onLocationSelect?: (location: LocationOption) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  // Style override props
  inputProps?: Omit<TextInputProps, 'value' | 'onChange' | 'onKeyDown' | 'onFocus' | 'error' | 'required' | 'disabled' | 'ref'>;
  dropdownProps?: Omit<PaperProps, 'ref'>;
  styles?: LocationAutocompleteStyles;
}

export default function LocationAutocomplete({
  value,
  onChange,
  onLocationSelect,
  placeholder = "Search for a city or location...",
  label = "Location",
  error,
  required,
  disabled,
  inputProps,
  dropdownProps,
  styles,
}: LocationAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Track current request to prevent race conditions
  const currentRequestRef = useRef<AbortController | null>(null);

  // Fetch suggestions from AWS Location Service
  const fetchSuggestions = async (query: string) => {
    // Cancel any existing request
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }

    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    currentRequestRef.current = abortController;

    setLoading(true);
    try {
      const response = await fetch(`/api/location/search?q=${encodeURIComponent(query)}`, {
        signal: abortController.signal
      });
      const data = await response.json();
      
      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        if (response.ok && data.places) {
          setSuggestions(data.places);
          setShowDropdown(data.places.length > 0);
          setSelectedIndex(-1);
        } else {
          setSuggestions([]);
          setShowDropdown(false);
        }
      }
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Location search error:', error);
        setSuggestions([]);
        setShowDropdown(false);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };


  // Handle input change with manual debouncing
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    onChange(newValue);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: LocationOption) => {
    // Cancel any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Cancel any pending requests immediately
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
      currentRequestRef.current = null;
    }
    
    // Clear everything immediately
    setSuggestions([]);
    setShowDropdown(false);
    setSelectedIndex(-1);
    setLoading(false);
    
    // Update value and callback
    onChange(suggestion.label);
    onLocationSelect?.(suggestion);
    
    // Blur input to prevent refocus
    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle input focus
  const handleFocus = () => {
    // Don't show dropdown immediately on focus - wait for user to type
  };

  // Handle click outside to close dropdown
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      !inputRef.current?.contains(event.target as Node)
    ) {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  };

  // Add event listener on mount, remove on unmount
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', ...styles?.container }}>
      <TextInput
        ref={inputRef}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        error={error}
        required={required}
        disabled={disabled}
        leftSection={<IconMapPin size={16} />}
        rightSection={loading ? <Loader size={16} /> : <IconSearch size={16} />}
        autoComplete="new-password"
        name="search-query"
        size="md"
        styles={{
          input: {
            paddingRight: loading ? '40px' : '40px',
            ...styles?.input,
          },
        }}
        {...inputProps}
      />

      {showDropdown && suggestions.length > 0 && (
        <Paper
          ref={dropdownRef}
          shadow="md"
          radius="md"
          data-testid="dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            marginTop: '4px',
            maxHeight: '300px',
            overflowY: 'auto',
            ...styles?.dropdown,
          }}
          {...dropdownProps}
        >
          <Stack gap={0}>
            {suggestions.map((suggestion, index) => (
              <div
                key={`${suggestion.value}-${index}`}
                onClick={() => handleSuggestionSelect(suggestion)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  backgroundColor: selectedIndex === index ? 'var(--mantine-color-blue-light)' : 'transparent',
                  borderRadius: selectedIndex === index ? '4px' : '0',
                  margin: selectedIndex === index ? '2px' : '0',
                  transition: 'all 0.15s ease',
                  ...styles?.suggestion,
                  ...(selectedIndex === index ? styles?.selectedSuggestion : {}),
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <Group gap="sm" wrap="nowrap">
                  <IconMapPin 
                    size={16} 
                    style={{ 
                      color: 'var(--mantine-color-blue-6)',
                      flexShrink: 0 
                    }} 
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text 
                      size="sm" 
                      style={{ 
                        wordBreak: 'break-word',
                        lineHeight: 1.4 
                      }}
                    >
                      {suggestion.label}
                    </Text>
                  </div>
                </Group>
              </div>
            ))}
          </Stack>
        </Paper>
      )}

      {showDropdown && suggestions.length === 0 && !loading && value.length >= 2 && (
        <Paper
          shadow="md"
          radius="md"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            marginTop: '4px',
            padding: '12px 16px',
            ...styles?.noResults,
          }}
        >
          <Text size="sm" c="dimmed" ta="center">
            No locations found for &quot;{value}&quot;
          </Text>
        </Paper>
      )}
    </div>
  );
}
